import axios from 'axios';

import Api from '@/api/config.ts';
import { apiUrls } from '@/api/api-urls.ts';
import { ChannelPolicy, Profile, UserInit } from '@/api/login/types.ts';
import {
  InitQueryParams,
  PreparePhotoResponse,
  PrepareVideoResponse,
  SearchFiltersPayload,
  UpdateCoordinatesPayload,
  UpdatePrivacyPayload,
  UpdateProfilePayload
} from '@/api/profile/types.ts';

const initProfile = async (queryParams: InitQueryParams) => {
  const formattedParams = {
    ...queryParams,
    offering_ids: queryParams.offering_ids
  };

  const response = await Api.get<UserInit>(apiUrls.profile.init, {
    params: formattedParams
  });

  return response.data;
};

const getProfile = async () => {
  const response = await Api.get<{ result: Profile }>(apiUrls.profile.get);

  return response.data.result;
};

const updateCoordinates = async (payload: UpdateCoordinatesPayload) => {
  const response = await Api.put<unknown>(
    apiUrls.profile.updateCoordinates,
    payload
  );

  return response.data;
};

const updatePrivacy = async (payload: UpdatePrivacyPayload) => {
  const response = await Api.put<unknown>(
    apiUrls.profile.updatePrivacy,
    payload
  );

  return response.data;
};

const updateProfile = async (payload: UpdateProfilePayload) => {
  const response = await Api.patch<Profile>(apiUrls.profile.update, payload);

  return response.data;
};

const deleteProfile = async () => {
  const response = await Api.delete<string>(apiUrls.profile.delete);

  return response.data;
};

const recoverProfile = async () => {
  const response = await Api.post<string>(apiUrls.profile.recover);

  return response.data;
};

const hideProfile = async () => {
  const response = await Api.post<string>(apiUrls.profile.hide);

  return response.data;
};

const showProfile = async () => {
  const response = await Api.post<string>(apiUrls.profile.show);

  return response.data;
};

const shadowProfile = async () => {
  const response = await Api.post<string>(apiUrls.profile.shadow);

  return response.data;
};

const unshadowProfile = async () => {
  const response = await Api.post<string>(apiUrls.profile.unshadow);

  return response.data;
};

const preparePhoto = async (mimeType: string, sizeBytes: number) => {
  const response = await Api.post<PreparePhotoResponse>(
    apiUrls.profile.preparePhoto,
    { mime_type: mimeType, size_bytes: sizeBytes }
  );

  return response.data;
};

const prepareSelfie = async (mimeType: string, sizeBytes: number) => {
  const response = await Api.put<PreparePhotoResponse>(
    apiUrls.profile.prepareSelfie,
    { mime_type: mimeType, size_bytes: sizeBytes }
  );

  return response.data;
};

const uploadToS3 = async (
  uploadUrl: string,
  file: Blob,
  mimeType: string,
  onProgress?: (progress: number) => void
) => {
  const response = await axios.put<unknown>(uploadUrl, file, {
    headers: {
      'Content-Type': mimeType
    },
    onUploadProgress: onProgress
      ? (progressEvent) => {
          const total = progressEvent.total ?? 0;
          const percent = Math.round((progressEvent.loaded * 100) / total);
          onProgress(percent);
        }
      : undefined
  });

  return response.data;
};

const deletePhoto = async (id: string) => {
  const response = await Api.delete<unknown>(apiUrls.profile.deletePhoto(id));

  return response.data;
};

const prepareVideo = async (mimeType: string, sizeBytes: number) => {
  const response = await Api.post<PrepareVideoResponse>(
    apiUrls.profile.prepareVideo,

    { mime_type: mimeType, size_bytes: sizeBytes }
  );

  return response.data;
};

const deleteVideo = async (hid: string) => {
  const response = await Api.delete<unknown>(apiUrls.profile.deleteVideo(hid));

  return response.data;
};

const submitPhotoToReview = async () => {
  const response = await Api.post<unknown>(apiUrls.profile.submitPhotoToReview);

  return response.data;
};

const completePhoto = async (
  hid: string,
  mimeType: string,
  sizeBytes: number
) => {
  const response = await Api.post<unknown>(apiUrls.profile.completePhoto(hid), {
    mime_type: mimeType,
    size_bytes: sizeBytes
  });

  return response.data;
};

const completeVideo = async (
  hid: string,
  mimeType: string,
  sizeBytes: number
) => {
  const response = await Api.post<unknown>(apiUrls.profile.completeVideo(hid), {
    mime_type: mimeType,
    size_bytes: sizeBytes
  });

  return response.data;
};

const handleFetchImage = async (imageUrl: string): Promise<File> => {
  const response = await Api.get<Blob>(imageUrl, { responseType: 'blob' });
  const blob = response.data;
  return new File([blob], 'image.jpg', { type: 'image/jpeg' });
};

const markOnline = async (payload: {
  device_id: string;
  tg_bot_version?: string;
  tg_platform?: string;
}) => {
  const response = await Api.post<unknown>(apiUrls.profile.markOnline, {
    ...payload
  });

  return response.data;
};

const updateLocale = async (payload: { locale: string }) => {
  const response = await Api.put<{ locale: string }>(
    apiUrls.profile.updateLocale,
    payload
  );

  return response.data;
};

const updatePolicy = async (payload: { channel_policy: ChannelPolicy }) => {
  const response = await Api.put<ChannelPolicy>(
    apiUrls.profile.channelPolicy,
    payload
  );

  return response.data;
};

const getPolicy = async () => {
  const response = await Api.get<{ channel_policy: ChannelPolicy }>(
    apiUrls.profile.channelPolicy
  );

  return response.data;
};

const updateSearchFilters = async (payload: SearchFiltersPayload) => {
  const response = await Api.put<unknown>(
    apiUrls.profile.updateSearchFilters,
    payload
  );

  return response.data;
};

const ProfileController = {
  initProfile,
  getProfile,
  updateProfile,
  deleteProfile,
  recoverProfile,
  hideProfile,
  showProfile,
  shadowProfile,
  unshadowProfile,
  preparePhoto,
  prepareSelfie,
  uploadToS3,
  deletePhoto,
  handleFetchImage,
  markOnline,
  updateLocale,
  updatePolicy,
  updatePrivacy,
  getPolicy,
  updateSearchFilters,
  updateCoordinates,
  submitPhotoToReview,
  prepareVideo,
  deleteVideo,
  completePhoto,
  completeVideo
};

export default ProfileController;
